import React, { useEffect, useState } from 'react';
import { Users, CheckCircle2, CalendarClock, CalendarCheck, XCircle, Download, Loader2, Search } from 'lucide-react';

import { Button } from '../../ui-v2';
import { Input, Select, KpiCard } from '../../ui-v2.5';

/**
 * FiltrosResumen — filtros del resumen de campaña (lote, empresa, negocio, departamento, estado) y
 * sus KPI, que llegan YA filtrados del servidor (la misma función que el CSV).
 *
 *   Total = Realizados + Sin cita + Con cita + Cancelados.
 *   Los KPI respetan lote, empresa, negocio y departamento, NO el estado; la lista y el CSV, todo.
 *
 * Un filtro con un solo valor posible no se pinta (no filtra nada), salvo que esté aplicado: entonces
 * se deja, para poder quitarlo. Los valores posibles vienen de TODOS los participantes, no de los
 * filtrados, así que un filtro no esconde las opciones de los demás.
 *
 * Colaboradoras: sólo lote, empresa colaboradora y estado (no tienen negocio ni departamento).
 *
 * Buscador (24/10): `q`, por nombre, negocio o departamento (en colaboradoras, la empresa). Se
 * filtra en el SERVIDOR, como los demás, para que los KPI y el CSV cuadren con lo que se ve. Se
 * manda al dejar de teclear 300 ms, no en cada tecla.
 */

const ESPERA_BUSCADOR_MS = 300;

/**
 * Rediseño (Fase 1): `conKpis={false}` deja solo la barra (los KPI van en la pestaña Resumen, con
 * KpisPersonas) y sin `onExportar` no se pinta el CSV (va en la pestaña Personas).
 */
export default function FiltrosResumen({
  ambito, disponibles, filtros, onChange, kpis, actualizando, onExportar = null, exportando, conKpis = true,
}) {
  // El texto se escribe aquí y se manda con una espera; si los filtros cambian por fuera
  // («Quitar filtros»), el campo se vacía con ellos.
  const [texto, setTexto] = useState(filtros.q || '');
  useEffect(() => { setTexto(filtros.q || ''); }, [filtros.q]);
  useEffect(() => {
    const limpio = texto.trim();
    if (limpio === (filtros.q || '')) return undefined;
    const t = setTimeout(() => onChange({ ...filtros, q: limpio || undefined }), ESPERA_BUSCADOR_MS);
    return () => clearTimeout(t);
  }, [texto, filtros, onChange]);

  const colab = ambito === 'colaborador';
  const claveEmpresa = colab ? 'empresa_colaboradora_id' : 'empresa_id';
  const d = disponibles || {};

  const campos = [
    {
      clave: 'lote_id', label: 'Lote',
      opciones: (d.lotes || []).map((l) => ({ value: String(l.id), label: `Lote ${l.numero} · ${l.nombre} (${l.total})` })),
    },
    {
      clave: claveEmpresa, label: colab ? 'Empresa colaboradora' : 'Empresa',
      opciones: (d.empresas || []).map((e) => ({ value: String(e.id), label: `${e.nombre} (${e.total})` })),
    },
    ...(colab ? [] : [
      {
        clave: 'negocio_id', label: 'Negocio',
        opciones: (d.negocios || []).map((n) => ({ value: String(n.id), label: `${n.nombre || `#${n.id}`} (${n.total})` })),
      },
      {
        clave: 'departamento', label: 'Departamento',
        opciones: (d.departamentos || []).map((x) => ({ value: x.nombre, label: `${x.nombre} (${x.total})` })),
      },
    ]),
    {
      clave: 'estado', label: 'Estado',
      opciones: (d.estados || []).map((e) => ({ value: e.id, label: `${e.nombre} (${e.total})` })),
    },
  ];
  const visibles = campos.filter((c) => c.opciones.length > 1 || filtros[c.clave]);
  const hayFiltro = Object.values(filtros).some(Boolean);

  return (
    <section className="mb-4 space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <Input
          label="Buscar"
          icon={Search}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder={colab ? 'Nombre o empresa' : 'Nombre, negocio o departamento'}
          maxLength={100}
          wrapperClassName="min-w-[240px]"
        />
        {visibles.map((c) => (
          <Select
            key={c.clave}
            label={c.label}
            value={filtros[c.clave] || ''}
            onChange={(e) => onChange({ ...filtros, [c.clave]: e.target.value || undefined })}
            wrapperClassName="min-w-[180px]"
          >
            <option value="">Todos</option>
            {c.opciones.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        ))}
        <div className="flex items-center gap-2 pb-0.5 ml-auto">
          {actualizando && <Loader2 className="w-4 h-4 animate-spin text-slate-400" aria-label="Actualizando" />}
          {hayFiltro && (
            <Button variant="ghost" size="sm" onClick={() => onChange({})}>Quitar filtros</Button>
          )}
          {onExportar && <BotonCsv onExportar={onExportar} exportando={exportando} />}
        </div>
      </div>

      {conKpis && <KpisPersonas kpis={kpis} filtros={filtros} />}
    </section>
  );
}

export function BotonCsv({ onExportar, exportando }) {
  return (
    <Button variant="outline" size="sm" onClick={onExportar} disabled={exportando}>
      {exportando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
      Exportar CSV
    </Button>
  );
}

/**
 * Los 5 KPI de personas. Respetan todos los filtros MENOS el estado (filtrar por un estado no pone a
 * cero los demás): la nota sólo sale si hay un filtro que les afecte. `alPulsar(clave)`, opcional:
 * cada KPI con destino lleva a su lista filtrada (las claves que no tienen filtro propio no se pulsan).
 */
export function KpisPersonas({ kpis, filtros = {}, alPulsar = null, destinos = {} }) {
  const k = kpis || { total: 0, realizados: 0, sin_cita: 0, con_cita: 0, cancelados: 0 };
  const nota = Object.entries(filtros).some(([c, v]) => v && c !== 'estado') ? 'con los filtros' : null;
  const click = (clave) => (alPulsar && destinos[clave] ? () => alPulsar(destinos[clave]) : undefined);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      <KpiCard icon={Users} label="Total" value={k.total} note={nota} onClick={click('total')} />
      <KpiCard icon={CheckCircle2} label="Realizados" value={k.realizados} tone="active" note={nota} onClick={click('realizados')} />
      <KpiCard icon={CalendarClock} label="Sin cita" value={k.sin_cita} tone="waiting" note={nota} onClick={click('sin_cita')} />
      <KpiCard icon={CalendarCheck} label="Con cita" value={k.con_cita} tone="progress" note={nota} onClick={click('con_cita')} />
      <KpiCard icon={XCircle} label="Cancelados" value={k.cancelados} tone="expired" note={nota} onClick={click('cancelados')} />
    </div>
  );
}
